/**
 * The guest session token.
 *
 * WHY MEMORY IS THE SOURCE OF TRUTH, and storage only a bonus:
 *
 * `POST /guest/invitations/:token/accept` returns the session token EXACTLY ONCE and there is no refresh
 * endpoint. `localStorage.setItem` THROWS in Safari private mode, under strict cookie blocking, and on
 * locked-down corporate machines — exactly the population a client portal serves. The first version of this
 * module swallowed that failure under a comment claiming the session would "live for this tab only", which
 * was false because nothing held it: such a guest accepted their invitation, had the token discarded, and
 * landed back on "you need an invitation link" holding a link they had just spent.
 *
 * So: write memory FIRST and unconditionally; attempt storage afterwards purely so the session survives a
 * reload. A storage failure degrades reload-survival, never the session itself.
 */

const KEY = 'autoryn.guest.session'

let memory: string | null = null

/** Best-effort storage handle — null whenever the browser refuses (private mode, blocked cookies, quota). */
function storage(): Storage | null {
  try {
    const s = window.localStorage
    // Touch it: some browsers expose the object and throw only on ACCESS.
    const probe = '__autoryn_probe__'
    s.setItem(probe, '1')
    s.removeItem(probe)
    return s
  } catch {
    return null
  }
}

export function setSession(token: string): void {
  memory = token                      // unconditional, and the thing every read prefers
  try { storage()?.setItem(KEY, token) } catch { /* reload-survival only */ }
}

export function getSession(): string | null {
  if (memory) return memory
  try {
    const fromStore = storage()?.getItem(KEY) ?? null
    if (fromStore) memory = fromStore // promote, so later reads never depend on storage again
    return fromStore
  } catch {
    return null
  }
}

export function clearSession(): void {
  memory = null
  try { storage()?.removeItem(KEY) } catch { /* nothing to do */ }
}

export function hasSession(): boolean {
  return getSession() !== null
}

/** Test seam: reset the in-memory half without touching storage. */
export function __resetMemoryForTests(): void {
  memory = null
}
